import { loadThresholds, type Thresholds } from '../../core/thresholds';

export type ScoreUnit = 'proficiency_level' | 'scale_score';

export interface PeSection {
  element: string;
  cohort_key: string;
  text: string;
  source: string;
}

export interface PeContent {
  unit_mode: ScoreUnit;
  sections: PeSection[];
}

export interface PeDistrictInfo {
  name: string;
  school_year: string;
  el_student_count: number;
  in_program_count?: number;
  exited_this_year_count?: number;
  el_student_count_last_year: number | null;
  school_count_with_els: number;
  data_window: string;
  /**
   * Students inside the district's monitoring window this year. Disjoint from
   * `el_student_count`, which counts students who exited during this year.
   * Optional because reports generated before the field existed do not carry
   * it — absent means "unknown", which is not the same as 0.
   */
  monitored_student_count?: number;
  /**
   * The same window a year earlier, derived from exit years alone. Null when
   * no student on record carries an exit year, in which case there is no
   * year-over-year line to show.
   */
  monitored_student_count_last_year?: number | null;
}

export interface PeDepartures {
  reclassified: number;
  moved_out: number;
  other: number;
  total: number;
  derived_count?: number;
  /** Exits the roster dated but gave no reason for, classified from EL status or ACCESS scores. */
  date_inferred_count?: number;
}

export interface PeEnrollmentYear {
  school_year: string;
  el_count: number;
  newcomer_count: number | null;
  ltel_count: number | null;
  newly_identified_count?: number | null;
  exited_count?: number | null;
}

export interface PeEnrollment {
  el_count_this_year: number;
  in_program_now?: number;
  el_count_last_year: number | null;
  net_change: number | null;
  new_identifications_this_year: number;
  departures_this_year: PeDepartures;
  exits_this_year?: PeDepartures;
  newcomer_count: number;
  newcomers_among_new_identifications?: number | null;
  ltel_count: number;
  multi_year_enrollment: PeEnrollmentYear[];
}

export interface PeTrajectoryPoint {
  school_year: string;
  average_scale_score: number;
  average_proficiency_level: number;
  reset_era: string;
}

export interface PeCompositeGrowth {
  active_window: string;
  active_window_description: string;
  first_post_reset_year?: boolean;
  district_average_scale_score_delta: number | null;
  district_average_scale_score_delta_last_year: number | null;
  district_average_proficiency_level_delta: number | null;
  district_average_proficiency_level_delta_last_year: number | null;
  current_average_proficiency_level: number | null;
  previous_average_proficiency_level: number | null;
  trajectory_chart_data: PeTrajectoryPoint[];
}

export interface PeDomainNotes {
  largest_gain_domain: string;
  largest_gap_domain: string;
  range_is_tight: boolean;
  range_scale_score_points: number;
}

export interface PeGrowth {
  composite: PeCompositeGrowth;
  by_domain: Record<string, PeCompositeGrowth>;
  by_domain_notes: PeDomainNotes;
}

export interface PeRateBlock {
  reclassified_count: number;
  eligible_count: number | null;
  rate: number | null;
}

export interface PeNullableRateBlock {
  reclassified_count: number | null;
  eligible_count: number | null;
  rate: number | null;
}

export interface PeRateYear {
  school_year: string;
  rate: number;
  reclassified_count: number;
  eligible_count: number;
}

export interface PePipelineProjection {
  status: string;
  placeholder_copy: string;
}

export interface PeReclassification {
  this_year: PeRateBlock;
  last_year: PeNullableRateBlock;
  median_years_in_program_for_reclassifiers: number | null;
  near_reclassification_count: number | null;
  review_eligible_count?: number | null;
  near_reclassification_by_unmet_domain: Record<string, number>;
  multi_year_rates: PeRateYear[];
  pipeline_projection: PePipelineProjection;
}

export interface PePreventionMetric {
  at_risk_count: number | null;
  subline_text: string;
  years_range_description: string;
  years_range_description_no_subject: string;
}

export interface PeLtelYear {
  school_year: string;
  count: number;
  newly_entered: number | null;
}

export interface PeLtel {
  count: number;
  percent_of_program: number;
  count_last_year: number | null;
  newly_entered_count: number;
  district_grown_count: number;
  prevention_metric: PePreventionMetric;
  multi_year_counts: PeLtelYear[];
}

export interface PeBenchmarkWindow {
  window: string;
  average_rubric_score: number | null;
  n_students: number;
}

export interface PeDomainCoverage {
  students_with_any_submission_this_year: number;
  el_student_total_this_year: number;
}

export interface PeDomainOverlayYear {
  school_year: string;
  benchmark_windows: PeBenchmarkWindow[];
  domain_coverage: PeDomainCoverage;
}

export interface PeFlashlight {
  is_integrated: boolean;
  scale_label: string;
  by_domain_overlay_data: Record<string, PeDomainOverlayYear[]>;
}

export interface PeSchool {
  school_name: string;
  school_type: string | null;
  el_count: number;
  current_member_count?: number | null;
  total_enrollment: number | null;
  el_share_of_enrollment: number | null;
  newcomer_count: number;
  newcomer_share_of_school_els: number;
  composite_growth_scale_score_delta: number | null;
  composite_growth_proficiency_level_delta: number | null;
  composite_growth_last_year: number | null;
  reclassification_rate: number | null;
  ltel_count: number;
  ltel_share_of_school_els: number;
  school_finding_eligible: boolean;
}

export interface PeCohortGrowthMetric {
  scale_score_delta: number | null;
  scale_score_delta_last_year: number | null;
  proficiency_level_delta: number | null;
  proficiency_level_delta_last_year: number | null;
}

export interface PeCohortGrowth {
  composite: PeCohortGrowthMetric;
  by_domain: Record<string, PeCohortGrowthMetric>;
}

export interface PeCohortComparison {
  growth_composite_delta: number;
  growth_composite_proficiency_delta: number | null;
  growth_by_domain_delta: Record<string, number>;
  has_non_cohort_comparison: boolean;
}

export interface PeCohortDomainAverage {
  domain: string;
  cohort_proficiency_level: number | null;
  non_cohort_proficiency_level: number | null;
  cohort_scale_score: number | null;
  non_cohort_scale_score: number | null;
}

export interface PeCohortBaseline {
  cohort_size: number;
  percent_of_program: number;
  year_over_year_change: number | null;
  growth: PeCohortGrowth;
  reclassification_within_cohort: PeRateBlock;
  comparison_to_non_cohort: PeCohortComparison;
  small_n_caveat: boolean;
  cohort_largest_growth_domain?: string | null;
  cohort_largest_growth_scale_score?: number | null;
  cohort_largest_growth_proficiency_level?: number | null;
  cohort_smallest_growth_domain?: string | null;
  cohort_smallest_growth_scale_score?: number | null;
  cohort_smallest_growth_proficiency_level?: number | null;
  cohort_dominant_unmet_domain?: string | null;
  domain_averages?: PeCohortDomainAverage[];
  district_grown_count?: number | null;
  newly_entered_count?: number | null;
}

export type PeCohortOrAbsent = Partial<PeCohortBaseline> & { absent?: boolean };

export interface PeSubgroups {
  ltel: PeCohortBaseline;
  newcomer: PeCohortBaseline;
  near_reclassification: PeCohortBaseline;
  review_eligible?: PeCohortBaseline;
  dually_identified: PeCohortOrAbsent;
  alternate_access: PeCohortOrAbsent;
}

export interface PeCrossSectionFinding {
  section: string;
  finding_type: string;
  magnitude: number;
  reference: string;
}

export interface PeDataQuality {
  students_missing_wida_history: number;
  students_missing_wida_history_active?: number;
  students_missing_wida_history_expected?: number;
  students_with_pre_reset_only: number;
  students_with_single_administration: number;
  students_tested_but_unrostered?: number;
  students_with_unknown_tenure?: number;
  wida_composite_coverage_pct: number;
  flashlight_coverage_pct: number;
  warnings: string[];
  notes?: string[] | null;
}

export interface PeDistrictConfigSnapshot {
  State: string;
  CompositeMinimum: number;
  DomainMinimums: Record<string, number>;
  LTELThreshold?: number;
  NewcomerMaxYears?: number;
  MonitoringYears?: number;
  SmallGroupThreshold?: number;
  SettingsStored?: boolean;
}

export type PeSettingSource = 'default' | 'district' | 'operator';

export interface PeCoverageSetting {
  value: number;
  source: PeSettingSource;
}

export interface PeCoverageLine {
  key: string;
  count: number;
  noun: string;
  text: string;
  sentence: string;
}

export interface PeDataCoverage {
  lines: PeCoverageLine[];
  settings: {
    newcomer_max_years: PeCoverageSetting;
    ltel_min_years: PeCoverageSetting;
    monitoring_years: PeCoverageSetting;
  };
  small_group_threshold: number;
}

export interface PeGroupStats {
  key: string;
  count: number;
  small: boolean;
  scored: number;
  scale_scored?: number;
  level: number | null;
  level_scale: number | null;
  growth: number | null;
  growth_scale: number | null;
  last_growth: number | null;
  last_growth_scale: number | null;
  met: number;
}

export interface PeStartGroup extends PeGroupStats {
  bucket: string;
  label: string;
}

export interface PeDomainRow {
  domain: string;
  label: string;
  scored?: number;
  level: number | null;
  growth: number | null;
  last_growth: number | null;
}

export interface PeCohortGradeRow extends PeGroupStats {
  cohort: string;
  label: string;
  grade: number;
  grade_label: string;
}

export interface PeSchoolGradeRow extends PeGroupStats {
  school: string;
}

export interface PeGradeDetail extends PeGroupStats {
  grade: number;
  grade_label: string;
  newcomers: number;
  ltel: number;
  others?: PeGroupStats;
  starts: PeStartGroup[];
  cohorts: PeCohortGradeRow[];
  domains: PeDomainRow[];
  schools: PeSchoolGradeRow[];
}

export interface PeSchoolGrade extends PeGroupStats {
  grade: number;
  grade_label: string;
  starts: PeStartGroup[];
}

export interface PeSchoolDetail extends PeGroupStats {
  school: string;
  kind: string;
  active_count?: number;
  newcomers: number;
  ltel: number;
  caveat: boolean;
  grades: PeSchoolGrade[];
}

export interface PeCohortSchoolRow {
  school: string;
  kind: string;
  key: string;
  count: number;
  of: number;
  met: number;
  small: boolean;
}

export interface PeGradeSpan {
  from: number;
  to: number;
  from_label: string;
  to_label: string;
  count: number;
}

export interface PeCohortDetail extends PeGroupStats {
  cohort: string;
  label: string;
  definition: string;
  share: number;
  last_count: number | null;
  awaiting_confirmation?: number;
  row_label: string;
  row_count: number | null;
  row_level: number | null;
  most: PeGradeSpan | null;
  by_grade: PeCohortGradeRow[];
  by_school: PeCohortSchoolRow[];
  by_years: { label: string; count: number }[] | null;
}

export interface PeHistoryYear {
  school_year: string;
  end_year: number;
  reset_era: string;
  grades: { grade: number; count: number; level: number; scale: number }[];
}

export interface PeDrilldown {
  grades: PeGradeDetail[];
  schools: PeSchoolDetail[];
  cohorts: PeCohortDetail[];
  history: PeHistoryYear[];
}

export interface PeMetadata {
  generated_at: string;
  el_student_count_used: number;
  reset_era_handling: string;
  district_config_snapshot?: PeDistrictConfigSnapshot;
}

export interface PeSchoolFindingCandidate {
  school_name: string;
  finding_type: string;
  magnitude: number;
  small_n_caveat: boolean;
  polarity: string;
}

export interface PeCandidateFindings {
  school_findings: PeSchoolFindingCandidate[] | null;
  subgroup_patterns: unknown[] | null;
  patterns_worth_investigating: unknown[] | null;
}

export interface PeReport {
  district: PeDistrictInfo;
  enrollment: PeEnrollment;
  growth: PeGrowth;
  flashlight: PeFlashlight;
  reclassification: PeReclassification;
  ltel: PeLtel;
  by_school: PeSchool[];
  subgroups: PeSubgroups;
  candidate_findings?: PeCandidateFindings;
  cross_section_top_findings: PeCrossSectionFinding[];
  data_quality: PeDataQuality;
  data_coverage?: PeDataCoverage;
  drilldown?: PeDrilldown | null;
  metadata: PeMetadata;
  cannot_generate: boolean;
  assessment_data_absent: boolean;
}

export function reportThresholds(metadata: PeMetadata): Thresholds {
  const snapshot = metadata.district_config_snapshot;
  const fallback = loadThresholds();
  return {
    ltelMinYears:
      snapshot?.LTELThreshold != null ? snapshot.LTELThreshold + 1 : fallback.ltelMinYears,
    newcomerMaxYears: snapshot?.NewcomerMaxYears ?? fallback.newcomerMaxYears,
    monitoringYears: snapshot?.MonitoringYears ?? fallback.monitoringYears,
  };
}

export const DEFAULT_SMALL_GROUP_THRESHOLD = 10;

export interface PeReportSettings extends Thresholds {
  smallGroupThreshold: number;
  sources: Record<keyof Thresholds, PeSettingSource> | null;
}

export function reportSettings(report: PeReport): PeReportSettings {
  const coverage = report.data_coverage;
  if (coverage?.settings) {
    const s = coverage.settings;
    return {
      newcomerMaxYears: s.newcomer_max_years.value,
      ltelMinYears: s.ltel_min_years.value,
      monitoringYears: s.monitoring_years.value,
      smallGroupThreshold: coverage.small_group_threshold || DEFAULT_SMALL_GROUP_THRESHOLD,
      sources: {
        newcomerMaxYears: s.newcomer_max_years.source,
        ltelMinYears: s.ltel_min_years.source,
        monitoringYears: s.monitoring_years.source,
      },
    };
  }
  return {
    ...reportThresholds(report.metadata),
    smallGroupThreshold:
      report.metadata.district_config_snapshot?.SmallGroupThreshold ?? DEFAULT_SMALL_GROUP_THRESHOLD,
    sources: null,
  };
}

export function findSection(
  content: PeContent,
  element: string,
  cohortKey?: string,
): PeSection | undefined {
  return content.sections.find(
    (s) => s.element === element && (cohortKey === undefined || s.cohort_key === cohortKey),
  );
}

export function findSections(content: PeContent, element: string): PeSection[] {
  return content.sections.filter((s) => s.element === element);
}
